import React, { FC, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { format, isValid, parseISO } from 'date-fns';
import styled from 'styled-components';
import { HormoneSummaryViewModel } from '../../../common/model/type/hormone-summary-status';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvSummaryStatus from '../molecules/OvSummaryStatus';
import { getOvulationPresentation } from './hormone-summary-presentation';
import OvCycleOverviewSummary from './OvCycleOverviewSummary';
import OvHormonePatternSummary from './OvHormonePatternSummary';

const EXPANDED_STORAGE_KEY = 'oova360.hormonesInContext.expanded';

const getInitialExpandedState = (): boolean => {
  try {
    return sessionStorage.getItem(EXPANDED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const storeExpandedState = (expanded: boolean): void => {
  try {
    sessionStorage.setItem(EXPANDED_STORAGE_KEY, String(expanded));
  } catch {
    return;
  }
};

const formatCycleRange = (startDay: string, endDay: string): string => {
  const startDate = parseISO(startDay);
  const endDate = parseISO(endDay);

  if (!isValid(startDate) || !isValid(endDate)) {
    return `${startDay} – ${endDay}`;
  }

  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`;
  }

  return `${format(startDate, 'MMM d, yyyy')} – ${format(
    endDate,
    'MMM d, yyyy'
  )}`;
};

type OvHormoneSummaryProps =
  | { state: 'loading' }
  | { state: 'error'; onRetry: () => void }
  | {
      state?: 'ready';
      summary: HormoneSummaryViewModel;
      selectedCycle: UserCycle;
    };

const OvHormoneSummary: FC<OvHormoneSummaryProps> = (props) => {
  const [expanded, setExpanded] = useState(getInitialExpandedState);

  if (props.state === 'loading') {
    return (
      <Container>
        <StateCard aria-busy="true" aria-label="Loading hormone summary">
          <Skeleton variant="text" width="14rem" height="2rem" />
          <Skeleton variant="text" width="10rem" />
          <SummarySkeletonContent data-testid="hormone-summary-skeleton-content">
            <PatternSkeletons>
              <Skeleton variant="rectangular" height="14rem" />
              <Skeleton variant="rectangular" height="14rem" />
            </PatternSkeletons>
            <Skeleton variant="rectangular" height="10rem" />
          </SummarySkeletonContent>
        </StateCard>
      </Container>
    );
  }

  if (props.state === 'error') {
    return (
      <Container>
        <StateCard>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={props.onRetry}>
                Retry
              </Button>
            }
          >
            We couldn&apos;t load Hormones in Context. The hormone charts remain
            available below.
          </Alert>
        </StateCard>
      </Container>
    );
  }

  const { summary, selectedCycle } = props;
  const ovulationPresentation = getOvulationPresentation(
    summary.cycleOverview.ovulationStatus
  );

  const handleExpandedChange = (
    _event: React.SyntheticEvent,
    value: boolean
  ) => {
    setExpanded(value);
    storeExpandedState(value);
  };

  return (
    <Container>
      <StyledAccordion
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={handleExpandedChange}
      >
        <StyledAccordionSummary
          id="hormones-in-context-header"
          aria-controls="hormones-in-context-content"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} Hormones in Context`}
          expandIcon={<ExpandMoreIcon aria-hidden="true" />}
        >
          <HeaderContent>
            <HeaderText>
              <Title>Hormones in Context</Title>
              <CycleRange>
                Selected cycle:{' '}
                {formatCycleRange(summary.startDay, summary.endDay)}
              </CycleRange>
            </HeaderText>
            <OvSummaryStatus presentation={ovulationPresentation} />
          </HeaderContent>
        </StyledAccordionSummary>
        <StyledAccordionDetails
          id="hormones-in-context-content"
          aria-labelledby="hormones-in-context-header"
        >
          <Content>
            <OvHormonePatternSummary
              follicularPhase={summary.follicularPhase}
              lutealPhase={summary.lutealPhase}
            />
            <Divider />
            <OvCycleOverviewSummary
              overview={summary.cycleOverview}
              selectedCycle={selectedCycle}
            />
          </Content>
        </StyledAccordionDetails>
      </StyledAccordion>
    </Container>
  );
};

export default OvHormoneSummary;

const Container = styled.section`
  container-type: inline-size;
  container-name: hormone-summary;
  min-width: 0;
`;

const StyledAccordion = styled(Accordion)`
  && {
    overflow: hidden;
    border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
    border-radius: ${Variables.borderRadius.LARGE};
    background: ${Colours.WHITE};
    box-shadow: ${Variables.boxShadow.alternateBox};

    &:before {
      display: none;
    }
  }
`;

const StateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 7rem;
  padding: 1rem;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
  background: ${Colours.WHITE};
  box-shadow: ${Variables.boxShadow.alternateBox};
`;

const SummarySkeletonContent = styled.div`
  display: grid;
  gap: 1rem;

  .MuiSkeleton-rectangular {
    border-radius: ${Variables.borderRadius.LARGE};
  }
`;

const PatternSkeletons = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;

  @container hormone-summary (min-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    min-height: 4.5rem;
    padding: 0.875rem 1rem;
    background: ${Colours.WHITE};

    &:hover,
    &.Mui-focusVisible {
      background: ${Colours.OV_WHITE_HOVER};
    }

    .MuiAccordionSummary-content {
      min-width: 0;
      margin: 0;
    }

    .MuiAccordionSummary-expandIconWrapper {
      margin-left: 0.75rem;
      color: ${Colours.OV_BASE};
    }
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;

  @container hormone-summary (max-width: 479px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.XLARGE};
  line-height: 1.5rem;
`;

const CycleRange = styled.span`
  display: block;
  margin-top: 0.25rem;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
  overflow-wrap: anywhere;
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  && {
    padding: 0 1rem 1rem;
    border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
    background: ${Colours.OV_WHITE};
  }
`;

const Content = styled.div`
  min-width: 0;
  padding-top: 0.875rem;
`;

const Divider = styled.hr`
  height: 1px;
  margin: 1rem 0;
  border: 0;
  background: ${Colours.OV_BORDER_COLOR_V3};
`;
